import React, { useContext, useState } from "react";
import { NameContext } from "../context/NameContext";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { MonoButton, PaperBG, UnderlineSketch } from "../kits/DesignKit";
import { FaLinkedin, FaInstagram, FaYoutube, FaGithub, FaExpand, FaStackOverflow } from "react-icons/fa";
import { socialMediaLinks } from "../config";
import ChangeNameModal from "./ChangeNameModal";
import SocialIconButton from "../components/SocialIconButton";
import MonoMacglbOver from "./MonoMacglbOver";
import { useAuth } from "../auth/AuthContext";
import ModalWrapper from "./utils/ModalWrapper";
import UpdateUserProfileModalOver from "./UpdateUserProfileModalOver";
import ContactFormOver from "./ContactFormOver";
import FaExpandAni from "./utils/FaExpandAni";

const UserOverlay = ({ onClose }) => {
  const { name } = useContext(NameContext);
  const { userId } = useAuth();


  const [showContactModal, setShowContactModal] = useState(false);
  const [showNameModal, setShowNameModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  // Check if we're on MediaBlog page or About page
  const isMediaBlogPage = location.pathname === "/zenlab2";
  const isAboutPage = location.pathname === "/about";

  // Get current tab from URL if on MediaBlog
  const getCurrentTab = () => {
    const params = new URLSearchParams(location.search);
    return params.get("tab") || "zendev";
  };

  const mediaBlogTabs = ["youtube", "blog", "blog2", "zendev", "CaseStudy", "Kurse"];
  const aboutTabs = [
    { id: "overview", label: "Überblick" },
    { id: "referenzen", label: "Referenzen" },
    { id: "kompass", label: "Kompass" }
  ];

  const handleTabClick = (tab) => {
    navigate(`/zenlab2?tab=${tab}`);
    onClose();
  };

  const handleAboutSectionClick = (sectionId) => {
    // Navigate to About page and scroll to section
    navigate(`/about#${sectionId}`);
    onClose();
    // Small delay to ensure navigation completes before scrolling
    setTimeout(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 100);
  };

const navLinks = [
  { name: "Moin", path: "/", isGreeting: true },
  {name: "Ich", path: "/about" },
    { name: "Dein ZenLab", path: "/zenlab2" },
  { name: "Nachricht an mich", isContactModal: true },

  userId && { name: "Deine Kurse", path: "/dashboard" },
  userId
    ? { name: "Benutzerdaten ändern", isProfileModal: true }
    : { name: "Dein Name ändern", isNameChange: true },
].filter(Boolean);

  return (
    <div className="fixed inset-0 z-[100] overflow-auto">
      <PaperBG className="min-h-screen flex flex-col items-center justify-center p-6 text-black font-mono relative">

        {/* Schließen-Button */}
        <button
          onClick={onClose}
          className="absolute top-8 right-8 text-gray-900 hover:text-black text-4xl md:text-3xl cursor-pointer hover:scale-110 rotate-90 transition-transform duration-300"
          aria-label="Schließen"
        >
          <FaExpandAni size={18} />
        </button>

        {/* Begrüßung */}
        <div className="flex flex-col items-center space-y-4 mt-2 mb-8">
          <MonoMacglbOver
            src="https://www.denisbitter.de/uploads/models/mac_1982.glb"
            title="Macintosh Classic (1982)"
            exposure="1"
          />
        </div>

        {/* MediaBlog Tabs - nur anzeigen wenn auf MediaBlog Seite */}
        {isMediaBlogPage && (
          <div className="w-full max-w-xs mx-auto mb-8">
            <h3 className="text-center text-sm font-mono mb-4 text-gray-700">ZenLab Kategorien</h3>
            <div className="flex flex-col gap-3">
              {mediaBlogTabs.map((tab) => {
                const currentTab = getCurrentTab();
                const isActive = currentTab === tab;
                return (
                  <button
                    key={tab}
                    onClick={() => handleTabClick(tab)}
                    className={`px-4 py-3 border rounded-lg font-mono text-xs transition-all ${
                      isActive
                        ? " bg-[#151515] text-orange-50 border-gray-900"
                        : "bg-white text-gray-700 border-gray-300 hover:border-gray-900 hover:text-black"
                    }`}
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* About Tabs - nur anzeigen wenn auf About Seite */}
        {isAboutPage && (
          <div className="w-full max-w-xs mx-auto mb-8">
            <h3 className="text-center text-sm font-mono mb-4 text-gray-700">About Bereiche</h3>
            <div className="flex flex-col gap-3">
              {aboutTabs.map((tab) => {
                const currentHash = location.hash.replace('#', '');
                const isActive = currentHash === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => handleAboutSectionClick(tab.id)}
                    className={`px-4 py-3 border rounded-lg font-mono text-xs transition-all ${
                      isActive
                        ? " bg-[#151515] text-orange-50 border-gray-900"
                        : "bg-white text-gray-700 border-gray-300 hover:border-gray-900 hover:text-black"
                    }`}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Menü wie Mobil */}
        <nav className="flex flex-col space-y-4 max-w-xs mx-auto text-center mb-10">
          {navLinks.map((link) => {
            if (link.isProfileModal) {
              // Nur wenn eingeloggt
              return (
                <button
                  key="profile-modal"
                  onClick={() => setShowProfileModal(true)}
                  className="relative text-gray-700 text-xs py-2 font-mono group transition-all hover:text-black"
                  type="button"
                >
                  {link.name}
                  <span className="absolute left-0 right-0 -bottom-2 flex justify-center pointer-events-none">
                    <div className="origin-left transition-transform duration-700 ease-out scale-x-0 group-hover:scale-x-100">
                      <UnderlineSketch color="#AC8E66" />
                    </div>
                  </span>
                </button>
              );
            }
            if (link.isNameChange) {
              // Für nicht-eingeloggt
              return (
                <button
                  key="name-change"
                  onClick={() => setShowNameModal(true)}
                  className="relative text-gray-800 text-xs py-2 font-mono group transition-all hover:text-black"
                  type="button"
                >
                  {link.name}
                  <span className="absolute left-0 right-0 -bottom-2 flex justify-center pointer-events-none">
                    <div className="origin-left transition-transform duration-700 ease-out scale-x-0 group-hover:scale-x-100">
                      <UnderlineSketch color="#AC8E66" />
                    </div>
                  </span>
                </button>
              );
            }

            if (link.isContactModal) {
              return (
                <button
                  key="contact-modal"
                  onClick={() => setShowContactModal(true)}
                  className="relative text-gray-800 text-xs py-2 font-mono group transition-all hover:text-black"
                  type="button"
                >
                  {link.name}
                  <span className="absolute left-0 right-0 -bottom-2 flex justify-center pointer-events-none">
                    <div className="origin-left transition-transform duration-700 ease-out scale-x-0 group-hover:scale-x-100">
                      <UnderlineSketch color="#AC8E66" />
                    </div>
                  </span>
                </button>
              );
            }
            // ...Login-Link und normale Links wie gehabt
            const isActive = location.pathname === link.path;
            return (
              <Link
                key={link.path}
                to={link.path}
                onClick={onClose}
                className={`relative text-gray-800 text-xs py-2 font-mono group transition-all ${
                  isActive ? "text-black" : "hover:text-black"
                }`}
              >
                {link.isGreeting && name ? `Moin, ${name}` : link.name}
                <span className="absolute left-0 right-0 -bottom-2 flex justify-center pointer-events-none">
                  <div
                    className={`origin-left transition-transform duration-700 ease-out ${
                      isActive ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
                    }`}
                  >
                    <UnderlineSketch color={isActive ? "#111827" : "#AC8E66"} />
                  </div>
                </span>
              </Link>
            );
          })}
        </nav>

        {/* Social Media Icons */}
        <div className="flex items-center space-x-4 text-gray-800 pt-4">
          <SocialIconButton href={socialMediaLinks.linkedin} Icon={FaLinkedin} />
          <SocialIconButton href={socialMediaLinks.youtube} Icon={FaYoutube} />
          <SocialIconButton href={socialMediaLinks.instagram} Icon={FaInstagram} />
          <SocialIconButton href={socialMediaLinks.github} Icon={FaGithub} />
          <SocialIconButton href={socialMediaLinks.stack} Icon={FaStackOverflow} />
        </div>

        {/* Modals – jeweils nur EINMAL */}
        {showNameModal && (
          <ChangeNameModal onClose={() => setShowNameModal(false)} />
        )}
        {showProfileModal && (
          <div className="fixed inset-0 bg-black bg-opacity-60 z-[999] flex items-center justify-center">
            <UpdateUserProfileModalOver
              onSuccess={() => setShowProfileModal(false)}
              onClose={() => setShowProfileModal(false)}
            />
          </div>
        )}
        {showContactModal && (
          <ModalWrapper onClose={() => setShowContactModal(false)}>
            <ContactFormOver onSuccess={() => setShowContactModal(false)} />
          </ModalWrapper>
        )}

      </PaperBG>
    </div>
  );
};

export default UserOverlay;
